import { call, put, select } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { AppAction, createReducer, RequestActionTypes } from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import {
  ACLProductPrivilege,
  ACLRecord,
  LoadProductPrivilegeListPayload,
  ProductPrivilegeListPayload,
} from 'models/ACL';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import {
  ACLActionTypes,
  getACLKey,
  initialState,
  loadProductPrivilegeListActions,
  selectACLContextCpRefNo,
  selectACLRequestCpRefNo,
  selectACLState,
  selectUserIdentifiersForUserAccessStoraging,
} from 'saga/acl/ducks';

export const productPrivileges = createReducer(initialState.productPrivileges, {
  [ACLActionTypes.LoadProductPrivilegeList]: {
    [RequestActionTypes.SUCCESS]: (
      state: Record<string, ACLProductPrivilege>,
      payload: { key: string; data: ACLProductPrivilege }
    ) => ({
      ...state,
      [payload.key]: {
        ...(state[payload.key] || {}),
        ...payload.data,
      },
    }),
  },
});

/* SELECTORS */
const selectACLSProductPrivileges = (state: AppState): Record<string, ACLProductPrivilege> =>
  selectACLState(state).productPrivileges;

const selectGetProductPrivilege = (
  state: AppState
): ((payload: ProductPrivilegeListPayload) => ACLRecord | undefined) => {
  const keyPart = selectUserIdentifiersForUserAccessStoraging(state);
  const contextCpRefNo = selectACLContextCpRefNo(state);

  return ({ entityRefNo, privilegeType, productCode }) => {
    const privileges = selectACLSProductPrivileges(state);
    const key = `${keyPart}|${contextCpRefNo ? entityRefNo : ''}`;

    if (privileges[key] && privileges[key][`${productCode}|${privilegeType}`]) {
      return privileges[key][`${productCode}|${privilegeType}`];
    }

    return undefined;
  };
};

export const selectExistsProductPrivileges = (state: AppState): ((payload: ProductPrivilegeListPayload) => boolean) => {
  return (payload) => !!selectGetProductPrivilege(state)(payload);
};

export const selectHasProductPrivilege = (state: AppState): ((payload: ProductPrivilegeListPayload) => boolean) => {
  return (payload) => selectGetProductPrivilege(state)(payload)?.isGranted || false;
};

/* SAGAS */
export function* loadProductPrivilegeList({ payload }: AppAction<LoadProductPrivilegeListPayload>) {
  const cpRefNo: string | undefined = yield select(selectACLRequestCpRefNo);
  const contextCpRefNo: string | undefined = yield select(selectACLContextCpRefNo);
  const entityRefNo = contextCpRefNo ? payload.entityRefNo : undefined;

  const request: ApiClientRequest = {
    apiCall: call(
      api.filterAclProductPrivilegeList,
      yield getApiUrl(environment.urlConfig.userAccess),
      payload.productCodeList,
      payload.privilegeTypeList,
      cpRefNo,
      entityRefNo,
      payload.forceFreshData
    ),
    skipUserActivityLog: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const keyPart: string = yield select(selectUserIdentifiersForUserAccessStoraging);

    const data = payload.productCodeList.reduce((acc: ACLProductPrivilege, productCode) => {
      payload.privilegeTypeList.forEach((privilegeType) => {
        acc[`${productCode}|${privilegeType}`] = {
          isGranted: resp.data[productCode]?.includes(privilegeType),
          recordTime: new Date(),
        };
      });

      return acc;
    }, {});

    yield put(
      loadProductPrivilegeListActions.success({
        data: data,
        key: getACLKey(keyPart, entityRefNo),
      })
    );
  } else {
    yield put(loadProductPrivilegeListActions.failure());
  }
}
